// ── DASH.B1 (#823) — redesigned three-zone Dashboard ────────────────────────
//
// Replaces the legacy projects-mirror DashboardPage (removed in #1639).
//
// Consumes GET /dashboard/overview (DASH.A1 #821 — live). The page reads the
// caller's role, looks up the role config, and renders three zones (Status
// Strip / Action Hub / Activity Feed) whose weighting varies by role. Loading
// → skeletons, error → friendly retry, 60s auto-refresh + a manual refresh
// button. Responsive: ≥1280px three-column, <1280px stacked (Zone 3 below
// Zone 2). One failing zone (ZoneResult.ok === false) degrades that zone only.

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useMyTenant } from '../hooks/useMyTenant';
import { useMediaQuery } from '../hooks/useMediaQuery';
import Sidebar from '../components/Sidebar';
import PushPermissionBanner from '../components/PushPermissionBanner';
import SetupGateNotice from '../components/SetupGateNotice';
import OnboardingPanel from '../components/OnboardingPanel';
import { registerServiceWorker } from '../lib/pushSubscribe';
import { API_BASE } from '../lib/api';
import { getDashboardRoleConfig, resolveDashboardRole } from '../config/dashboardRoleConfig';
import { isInPartnerContext } from '../lib/partnerContext';
import DashboardZone1Status from '../components/dashboard/DashboardZone1Status';
import DashboardZone2Action from '../components/dashboard/DashboardZone2Action';
import DashboardZone3Activity, { type ActivityItem } from '../components/dashboard/DashboardZone3Activity';
import LandingModal from '../components/dashboard/LandingModal';
import { hasDegradedZone, normalizeOverview, type DashboardOverview } from '../types/dashboard';

const AUTO_REFRESH_MS = 60_000; // §AC — auto-refresh every 60 seconds
const DESKTOP_QUERY = '(min-width: 1280px)';

// GET /dashboard/activity response item (#822/#1600). apps/web cannot import
// apps/api types directly — mirrors the DashboardOverview duplication pattern
// in types/dashboard.ts. Source of truth: activityFeedService.ts's ActivityItem.
interface RawActivityItem {
  id: string;
  eventType: string;
  summary: string;
  timestamp: string;
  contextUrl: string | null;
  projectId: string | null;
}

function isRawActivityItem(value: unknown): value is RawActivityItem {
  if (value === null || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.id === 'string' &&
    typeof v.eventType === 'string' &&
    typeof v.summary === 'string' &&
    typeof v.timestamp === 'string'
  );
}

// contextUrl is null whenever a row has no projectId (buildContextUrl in
// activityFeedService.ts) — DashboardZone3Activity feeds it straight into
// <Link to=...>, so null must be coalesced before it gets there. projectId
// itself is unused by the component and dropped. Malformed entries are
// dropped rather than coerced — mirrors normalizeOverview's asZone hygiene
// (never trust the wire shape) for this sibling endpoint.
function normalizeActivityItems(rawItems: unknown): ActivityItem[] {
  if (!Array.isArray(rawItems)) return [];
  return rawItems.filter(isRawActivityItem).map((item) => ({
    id: item.id,
    eventType: item.eventType,
    summary: item.summary,
    timestamp: item.timestamp,
    contextUrl: item.contextUrl ?? '#',
  }));
}

export default function Dashboard() {
  const { token } = useAuth();
  const { myTenant } = useMyTenant();
  const navigate = useNavigate();
  // DASH.C1 (#825) — first-login landing-choice modal, shown on ?firstLogin=true.
  const [searchParams] = useSearchParams();
  const [landingModalDismissed, setLandingModalDismissed] = useState(false);

  const [overview, setOverview] = useState<DashboardOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [activityItems, setActivityItems] = useState<ActivityItem[]>([]);
  // True until the FIRST activity fetch settles (success or failure), then
  // false forever — mirrors `loading`'s one-shot semantics (not `refreshing`'s
  // per-cycle one). Without this, Zone 3 flashes its empty state between
  // /overview resolving (which clears the page-level `loading`) and the
  // separate /activity round-trip actually finishing.
  const [activityLoading, setActivityLoading] = useState(true);

  // Monotonic request id + mounted flag so overlapping fetches (60s auto-refresh
  // racing a manual refresh, or a fetch resolving after unmount) can't commit
  // stale results over fresh ones. Only the latest in-flight request commits.
  const requestSeq = useRef(0);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const isDesktop = useMediaQuery(DESKTOP_QUERY);
  // Gate the partner variant on ACTIVE partner-context (partnerContextToken),
  // not isPartner account-membership — see resolveDashboardRole / #1315.
  const role = resolveDashboardRole(myTenant, { partnerContext: isInPartnerContext() });
  const showLandingModal = searchParams.get('firstLogin') === 'true' && !landingModalDismissed;
  const config = getDashboardRoleConfig(role);
  const template = isDesktop ? config.gridTemplate.desktop : config.gridTemplate.mobile;

  // Register the push service worker once on mount (idempotent no-op without
  // Push API support) — mirrors the legacy DashboardPage.
  useEffect(() => {
    void registerServiceWorker();
  }, []);

  // DASH.A2 (#822) / #1600 — Zone 3 activity feed. Rides the same requestSeq
  // (passed in by load(), below) so it shares one staleness guard with
  // /overview rather than racing its own timer. EVERY failure — network,
  // non-2xx, including a 403 — is swallowed identically: this is a
  // degrade-in-place zone, so it must never surface the whole-page error
  // screen or force a navigation. /overview's own 403 handling (in load(),
  // below) is the sole authority for "the user lost access, leave the page."
  const loadActivity = useCallback(
    async (seq: number, endpoint: string): Promise<void> => {
      const isStale = (): boolean => !mountedRef.current || seq !== requestSeq.current;
      try {
        // The endpoint string comes from the server-advertised
        // activity_feed_endpoint field (normalizeOverview only checks it's a
        // string). Defense in depth: refuse to dereference it into a
        // credentialed fetch unless it's a same-origin relative path.
        if (!endpoint.startsWith('/')) return;

        const res = await fetch(`${API_BASE}${endpoint}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          credentials: 'include',
          cache: 'no-cache',
        });
        if (isStale()) return;
        if (!res.ok) throw new Error('Failed to load activity');

        const body = await res.json();
        const items = normalizeActivityItems((body as { items?: unknown } | null)?.items);
        if (isStale()) return;
        setActivityItems(items);
      } catch {
        // Swallow — see comment above.
      } finally {
        if (!isStale()) setActivityLoading(false);
      }
    },
    [token],
  );

  const load = useCallback(
    async (mode: 'initial' | 'refresh'): Promise<void> => {
      const seq = ++requestSeq.current;
      // Stale = a newer load() started, or the component unmounted, since this
      // request began. Stale requests must not commit any state.
      const isStale = (): boolean => !mountedRef.current || seq !== requestSeq.current;

      if (mode === 'refresh') setRefreshing(true);
      try {
        const res = await fetch(`${API_BASE}/dashboard/overview`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          credentials: 'include',
          // #1330: /overview is a per-token (private) resource, but the browser
          // HTTP cache keys on URL only (not Authorization). With Cache-Control
          // max-age=30, a cached own-org response could be served after a
          // partner context switch (same URL, new token). 'no-cache' forces a
          // conditional revalidation so the server re-scopes by the request
          // token. The 304 path is tenant-SAFE: the server ETag is a content
          // hash (sha256 of the per-tenant JSON — dashboard.routes.ts), so a
          // context switch yields different content → different ETag → 200 with
          // the client's data, never a 304 carrying the prior tenant's bytes.
          cache: 'no-cache',
        });
        if (isStale()) return;

        // No accessible tenants → clear renderable state so stale data can't
        // flash after access is lost, then bounce to /projects (legacy behaviour).
        if (res.status === 403) {
          setOverview(null);
          setError(null);
          navigate('/projects');
          return;
        }
        if (!res.ok) throw new Error('Failed to load dashboard');

        const data = normalizeOverview(await res.json());
        if (isStale()) return;
        setOverview(data);
        setError(null);

        // #1600: skip the call entirely when Zone 3 is hidden for this role
        // (config.zone3.visible === false) — no point fetching data nothing
        // will render.
        if (config.zone3.visible) {
          void loadActivity(seq, data.activity_feed_endpoint);
        }
      } catch (err) {
        if (isStale()) return;
        // Keep showing stale data on a failed refresh; only surface the error
        // screen when we have nothing to render.
        setError(err instanceof Error ? err.message : 'Failed to load dashboard');
      } finally {
        if (!isStale()) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    [token, navigate, config.zone3.visible, loadActivity],
  );

  useEffect(() => {
    void load('initial');
  }, [load]);

  // 60s auto-refresh.
  useEffect(() => {
    const id = window.setInterval(() => {
      void load('refresh');
    }, AUTO_REFRESH_MS);
    return () => window.clearInterval(id);
  }, [load]);

  const handleManualRefresh = (): void => {
    void load('refresh');
  };

  const handleRetry = (): void => {
    setLoading(true);
    setError(null);
    void load('initial');
  };

  const showErrorScreen = error !== null && overview === null;

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--bg)' }}>
      {showLandingModal && (
        <LandingModal role={role} onClose={() => setLandingModalDismissed(true)} />
      )}
      <Sidebar />
      <main style={{ flex: 1, padding: 32 }} data-testid="dashboard-v2">
        <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0, fontFamily: 'var(--font-display)' }}>Dashboard</h1>
          <button
            type="button"
            data-testid="dashboard-refresh"
            onClick={handleManualRefresh}
            disabled={refreshing}
            style={{
              padding: '8px 16px',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border)',
              background: 'var(--surface)',
              color: 'var(--text)',
              cursor: refreshing ? 'default' : 'pointer',
              fontSize: 14,
              fontWeight: 500,
              opacity: refreshing ? 0.6 : 1,
            }}
          >
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </header>

        <PushPermissionBanner />
        {/* ONB.C1 — flash banner if the /setup gate bounced an ineligible user here. */}
        <SetupGateNotice />
        {/* #1598 — same relationship as legacy DashboardPage.tsx: self-fetching,
            self-gating, auto-hides when no active setup_project exists. */}
        <OnboardingPanel />

        {showErrorScreen ? (
          <div data-testid="dashboard-error" style={{ textAlign: 'center', padding: '64px 0' }}>
            <p style={{ fontSize: 16, color: 'var(--text)', marginBottom: 8 }}>We couldn&rsquo;t load your dashboard.</p>
            <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 16 }}>{error}</p>
            <button
              type="button"
              onClick={handleRetry}
              style={{
                padding: '8px 20px',
                borderRadius: 'var(--radius-md)',
                background: 'var(--primary)',
                color: '#fff',
                border: 'none',
                cursor: 'pointer',
                fontWeight: 500,
              }}
            >
              Try again
            </button>
          </div>
        ) : overview || loading ? (
          // Render the zones only when we have data or are still loading. After a
          // 403 the page navigates away with overview=null/loading=false — don't
          // fall through to the grid (the zones would read a null overview).
          <>
            {overview && hasDegradedZone(overview) && (
              <div
                data-testid="dashboard-degraded"
                role="status"
                style={{
                  marginBottom: 16,
                  padding: '10px 14px',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--warning)',
                  background: 'var(--surface-alt)',
                  color: 'var(--text-secondary)',
                  fontSize: 13,
                }}
              >
                Some sections couldn&rsquo;t be refreshed. Showing what we have.
              </div>
            )}

            <div
              data-testid="dashboard-grid"
              style={{
                display: 'grid',
                gridTemplateAreas: template.areas,
                gridTemplateColumns: template.columns,
                gap: 16,
                alignItems: 'start',
              }}
            >
              <section aria-label="Status overview" style={{ gridArea: 'zone1' }}>
                <DashboardZone1Status tiles={config.zone1.tiles} overview={overview} loading={loading} />
              </section>

              <section aria-label="Action hub" style={{ gridArea: 'zone2' }}>
                <DashboardZone2Action cards={config.zone2.cards} overview={overview} loading={loading} />
              </section>

              {config.zone3.visible && (
                <section aria-label="Activity feed" style={{ gridArea: 'zone3' }}>
                  <DashboardZone3Activity
                    overview={overview}
                    loading={loading}
                    density={config.zone3.density}
                    items={activityItems}
                    itemsLoading={activityLoading}
                  />
                </section>
              )}
            </div>
          </>
        ) : null}
      </main>
    </div>
  );
}
